import React, { useState, useEffect, useCallback } from 'react';
import { useAuthToken } from '../utils/authTokenManager';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Wrench,
  Shield,
  Users,
  AlertCircle,
  Activity,
  CheckCircle,
  Menu,
  X,
  LogOut,
  User,
  Settings,
  RefreshCw,
  MessageCircle
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth0Context } from '../contexts/Auth0Context';
import { PROCOGIA_COLORS } from '../utils/themeUtils';
import axios from 'axios';
import procogiaLogoLight from '../assets/images/procogia-logo-light.png';
import procogiaLogoDark from '../assets/images/procogia-logo-dark.png';

const Layout = ({ children }) => {
  const { getThemeClasses, isDark } = useTheme();
  const location = useLocation();
  const { appUser, logout, switchProfile, auth0User, hasAnyRole, getAccessTokenSilently } = useAuth0Context();
  const themeClasses = getThemeClasses();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalEquipment: 700,
    activeEquipment: 529,
    criticalAlerts: 0
  });
  const [showUserMenu, setShowUserMenu] = useState(false);

  const fetchStats = async (token) => {
    console.log('🔍 [DEBUG] fetchStats called - starting sidebar stats fetch...');
    try {
      console.log('✅ [DEBUG] Auth0 token acquired for sidebar stats', { 
        tokenLength: token?.length || 0,
        hasToken: !!token
      });
      
      console.log('🌐 [DEBUG] Making alerts API request for sidebar...', {
        url: 'alerts?limit=5000',
        baseURL: axios.defaults.baseURL,
        fullURL: `${axios.defaults.baseURL}/alerts?limit=5000`
      });
      
      const response = await axios.get('alerts?limit=5000', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('📊 [DEBUG] Sidebar alerts API response:', {
        status: response.status,
        data: response.data,
        dataType: typeof response.data,
        dataLength: response.data?.length || 0,
        isArray: Array.isArray(response.data),
        firstItem: response.data?.[0] || response.data?.alerts?.[0],
        responseKeys: response.data ? Object.keys(response.data) : []
      });
      
      // Handle different possible response structures
      let alerts = [];
      if (Array.isArray(response.data)) {
        alerts = response.data;
      } else if (response.data?.alerts && Array.isArray(response.data.alerts)) {
        alerts = response.data.alerts;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        alerts = response.data.data;
      } else {
        console.warn('⚠️ [DEBUG] Unexpected API response structure:', response.data);
        alerts = [];
      }
      
      console.log('🔍 [DEBUG] Processing alerts for sidebar stats...', {
        totalAlerts: alerts.length,
        sampleAlert: alerts[0],
        alertsType: typeof alerts
      });
      
      const criticalCount = alerts.filter(a => {
        const isCritical = a && a.type === 'critical' && a.status === 'active';
        if (isCritical) {
          console.log('✅ [DEBUG] Found critical alert:', {
            id: a.id,
            type: a.type,
            status: a.status,
            title: a.title
          });
        }
        return isCritical;
      }).length;
      
      console.log('📈 [DEBUG] Sidebar critical count calculated:', {
        criticalCount,
        previousStats: stats
      });
      
      setStats(prev => {
        const newStats = {
          ...prev,
          criticalAlerts: criticalCount
        };
        console.log('🔄 [DEBUG] Updating sidebar stats:', {
          previous: prev,
          new: newStats
        });
        return newStats;
      });
      
      console.log('✅ [DEBUG] Sidebar stats updated successfully!');
    } catch (error) {
      console.error('❌ [DEBUG] Error fetching sidebar stats:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.url
      });
      
      // Set fallback data on error
      setStats(prev => ({
        ...prev,
        criticalAlerts: 0
      }));
    }
  };

  // NUCLEAR FIX: Use centralized token manager (NO HOOK VIOLATIONS)
  const authToken = useAuthToken(appUser, getAccessTokenSilently);

  // Load stats when we have both user and token
  useEffect(() => {
    if (appUser && authToken) {
      fetchStats(authToken);
    }
  }, [appUser, authToken]);

  const handleLogout = () => {
    logout();
  };

  const handleSwitchProfile = () => {
    switchProfile();
  };

  const getUserAvatar = () => {
    if (auth0User?.picture) {
      return auth0User.picture;
    }
    if (appUser?.avatar) {
      return appUser.avatar;
    }
    return null;
  };

  const getUserDisplayName = () => {
    if (auth0User?.given_name) {
      return auth0User.given_name;
    }
    if (auth0User?.name) {
      return auth0User.name;
    }
    if (appUser?.name) {
      return appUser.name.split(' ')[0];
    }
    return 'User';
  };

  const sidebarItems = [
    {
      icon: <Building2 className="h-5 w-5" />,
      name: 'Overview',
      href: '/overview',
      active: location.pathname === '/' || location.pathname === '/overview',
      allowedRoles: [] // Everyone can access Overview
    },
    {
      icon: <Users className="h-5 w-5" />,
      name: 'Users',
      href: '/users',
      active: location.pathname === '/users',
      allowedRoles: ['admin', 'operations_manager'] // Only Admin and Operations Manager
    },
    {
      icon: <AlertCircle className="h-5 w-5" />,
      name: 'Alerts',
      href: '/alerts',
      active: location.pathname === '/alerts',
      allowedRoles: [] // Everyone can access Alerts
    },
    {
      icon: <Wrench className="h-5 w-5" />,
      name: 'Equipment',
      href: '/equipment-dashboard',
      active: location.pathname === '/equipment-dashboard' || location.pathname === '/equipment',
      allowedRoles: [] // Everyone can access Equipment
    },
    {
      icon: <MessageCircle className="h-5 w-5" />,
      name: 'AI Chat',
      href: '/chat',
      active: location.pathname === '/chat',
      allowedRoles: [] // Everyone can access AI Chat
    },
    {
      icon: <Shield className="h-5 w-5" />,
      name: 'System Testing',
      href: '/system-testing',
      active: location.pathname === '/system-testing' || location.pathname === '/api-test',
      allowedRoles: ['admin', 'operations_manager'] // Only Admin and Operations Manager
    }
  ];

  // Filter sidebar items based on user role
  const filteredSidebarItems = sidebarItems.filter(item => {
    // If no roles specified, everyone can access
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    
    // For items with specific role requirements, check if user has access
    if (!appUser?.role) {
      return false; // No user role, deny access to restricted items
    }
    
    // Admin has access to everything
    if (appUser.role === 'admin') {
      return true;
    }
    
    // Check if user's role is in the allowedRoles array
    return item.allowedRoles.includes(appUser.role);
  });
  
  const userMenuItems = [
    {
      icon: <User className="h-4 w-4" />,
      label: 'Profile Settings',
      action: () => console.log('Profile settings')
    },
    {
      icon: <RefreshCw className="h-4 w-4" />,
      label: 'Switch Role',
      action: handleSwitchProfile
    },
    {
      icon: <Settings className="h-4 w-4" />,
      label: 'Settings',
      action: () => console.log('Settings')
    },
    {
      icon: <LogOut className="h-4 w-4" />,
      label: 'Sign Out',
      action: handleLogout,
      className: 'text-red-600 hover:text-red-700'
    }
  ];

  return (
    <div className={`flex h-screen ${themeClasses.bg.primary}`}>
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 ${themeClasses.bg.secondary} ${themeClasses.border.primary} border-r transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center">
            <img 
              src={isDark ? procogiaLogoDark : procogiaLogoLight} 
              alt="ProCogia Logo" 
              className="h-8 w-auto"
            />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className={`lg:hidden p-2 rounded-md ${themeClasses.text.secondary} hover:${themeClasses.bg.tertiary}`}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Equipment Stats */}
        <div className="px-4 py-4 space-y-3">
          <div className={`p-3 rounded-lg ${themeClasses.bg.tertiary}`}>
            <button className="w-full flex items-center justify-between text-left">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                <span className={`text-sm font-medium ${themeClasses.text.secondary}`}>
                  Total Equipment
                </span>
              </div>
              <span className={`text-lg font-bold ${themeClasses.text.primary}`}>
                {stats.totalEquipment}
              </span>
            </button>
          </div>

          <div className={`p-3 rounded-lg ${themeClasses.bg.tertiary}`}>
            <button className="w-full flex items-center justify-between text-left">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={`text-sm font-medium ${themeClasses.text.secondary}`}>
                  Active/Operational
                </span>
              </div>
              <span className={`text-lg font-bold ${themeClasses.text.primary}`}>
                {stats.activeEquipment}
              </span>
            </button>
          </div>

          <div className={`p-3 rounded-lg ${themeClasses.bg.tertiary}`}>
            <button className="w-full flex items-center justify-between text-left">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className={`text-sm font-medium ${themeClasses.text.secondary}`}>
                  Critical Alerts
                </span>
              </div>
              <span className={`text-lg font-bold ${themeClasses.text.primary}`}>
                {stats.criticalAlerts}
              </span>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-4 space-y-2">
          {filteredSidebarItems.map((item, index) => (
            <Link
              key={index}
              to={item.href}
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                item.active 
                  ? `${themeClasses.bg.accent} ${themeClasses.text.accent}` 
                  : `${themeClasses.text.secondary} hover:${themeClasses.bg.tertiary}`
              }`}
            >
              {item.icon}
              <span className="font-medium">{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* Role Badge */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className={`p-3 rounded-lg ${themeClasses.bg.tertiary} text-center`}>
            <div className="text-xs text-gray-500 mb-1">Current Role</div>
            <div className={`text-sm font-semibold ${themeClasses.text.primary}`}>
              {appUser?.name || 'User'}
            </div>
            <div className="text-xs text-gray-400">{appUser?.department || 'Department'}</div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className={`${themeClasses.bg.primary} shadow-sm border-b border-gray-200 px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              <div className="flex items-center space-x-4">
                <h1 className={`text-xl font-semibold ${themeClasses.text.primary}`}>
                  {appUser?.department || 'Dashboard'}
                </h1>
                
                {/* Current Role Badge with Switch Button */}
                <div 
                  className="flex items-center space-x-2 px-3 py-1 rounded-full border"
                  style={{
                    backgroundColor: '#e6f7fa',
                    borderColor: '#b3e5fc'
                  }}
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: PROCOGIA_COLORS.blue }}
                  ></div>
                  <span 
                    className="text-sm font-medium"
                    style={{ color: PROCOGIA_COLORS.blue }}
                  >
                    {appUser?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'User'}
                  </span>
                  <button
                    onClick={handleSwitchProfile}
                    className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-100 transition-colors"
                    title="Switch to different role"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Stats Display */}
              <div className="hidden md:flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4 text-green-500" />
                  <span>Equipment: {stats.activeEquipment}/{stats.totalEquipment}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  <span>Critical: {stats.criticalAlerts}</span>
                </div>
              </div>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {getUserAvatar() ? (
                    <img 
                      src={getUserAvatar()} 
                      alt="User avatar" 
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {getUserDisplayName().charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="text-left">
                    <div className={`text-sm font-medium ${themeClasses.text.primary}`}>
                      {getUserDisplayName()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {appUser?.name || 'User Profile'}
                    </div>
                  </div>
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">{getUserDisplayName()}</div>
                      <div className="text-xs text-gray-500">{appUser?.email}</div>
                    </div>
                    
                    {userMenuItems.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setShowUserMenu(false);
                          item.action();
                        }}
                        className={`flex items-center space-x-3 w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${item.className || 'text-gray-700'}`}
                      >
                        {item.icon}
                        <span className="text-sm">{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>



      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 